// TASK-030: Per-user language preference (localStorage + Supabase profiles.language)
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';

const LANGUAGE_STORAGE_KEY = 'language';

type Language = 'zh' | 'en';

function getStoredLanguage(): Language {
  try {
    const s = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return s === 'zh' || s === 'en' ? s : 'zh';
  } catch {
    return 'zh';
  }
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  zh: {
    // Header
    'selectCompany': '公司列表',
    'chooseCompany': '請選擇您要存取的公司',
    'switchCompany': '切換公司',
    'logout': '登出',
    'settings': '設定',
    'welcomeBack': '歡迎回來',
    'welcome': '您好',
    'clickToAccess': '點擊進入',
    'noAccess': '無權限',
    'adminSettings': '管理員設定',
    'userManagement': '用戶管理',
    'manageUserEmails': '管理用戶電子郵件和密碼',
    'roleAssignment': '權限分配',
    'assignUserRoles': '分配權限和公司權限',
    'companyLogos': '公司標誌',
    'uploadManageLogos': '管理公司標誌',
    'settingsFutureVersions': '設定功能將在未來版本中實現',
    'accessingCompany': '正在進入',
    'noAccessCompany': '您沒有該公司的使用權限',
    'loggedOutSuccessfully': '已成功登出',
    
    // Categories
    'orderManagement': '訂單管理',
    'productOverview': '產品總覽', 
    'purchaseSuggestions': '採購建議',
    'expirySummary': '到期日總表',
    'promotions': '推廣活動',
    'analytics': '綜合統計',
    'storeSettings': '店舖設定',
    'headquarters': '總公司',
    'hqPurchasing': '採購',
    'hqWholesale': '批發',
    'hqAccounting': '會計',

    // Order Management
    'orderOverview': '訂單總覽',
    'salesStats': '銷售統計',
    'salesImport': '滙入功能（銷售記錄）',
    
    // Product Overview
    'productMgmt': '產品管理',
    'inventoryMgmt': '庫存管理',
    'productStats': '產品統計',
    'productExpiry': '到期日總表',
    'productImport': '滙入功能（產品資料 - HKTVmall）',
    'productStockImport': '滙入資料（產品存量 - HKTVmall）',
    'productPendingImport': '滙入功能（產品成本）',
    'product3PLDataImport': '滙入功能（3PL 物流倉存量）',
    
    // Purchase Suggestions
    'suggestedOrders': '建議採購',
    'crossCompanySuggestions': '跨公司採購建議',
    'ruleSettings': '細則設定',
    'purchaseAnalysis': '採購統計',
    'purchaseImport': '滙入功能（採購）',
    
    // Expiry Summary
    'expiringSoon': '快到期產品',
    'expiryHandling': '到期日核對',
    'bestExpiryImport': '匯入功能(最佳使用日期)',
    'bestExpiryImportDesc': 'Excel 匯入後自動分析並比對到期日期',
    'columnIdentification': '欄位識別',
    'skuColumn': 'SKU 欄位',
    'dateColumn': '日期欄位',
    'processingStatus': '處理狀態',
    'resultsSummary': '結果摘要',
    'totalRecords': '總記錄',
    'validRecords': '有效記錄',
    'skippedRecords': '跳過記錄',
    'matchedRecords': '匹配記錄',
    'itemsToReview': '需注意項目',
    'remindersList': '提醒列表',
    'expiryMismatch': '到期日不一致',
    'excelExpiryDate': 'Excel 到期日',
    'systemExpiryDate': '系統到期日',
    'columnNotFound': '找不到必要的欄位',
    'supportedColumns': '支援的欄位名稱',
    'skuColumnNames': 'SKU 欄位: 条码, Barcode',
    'dateColumnNames': '日期欄位: 批次号, 有效日期, Expiry Date',
    'invalidDataSkipped': '無效資料已跳過',
    'processingComplete': '處理完成',
    
    // Promotions
    'campaignOverview': '活動總覽',
    'searchCampaigns': '搜尋活動',
    'promotionImport': '滙入功能（推廣活動）',
    
    // Analytics
    'salesAnalysis': '銷售分析',
    'stockAnalysis': '存貨分析',
    'slowMoving': '慢流產品分析',
    'trafficAnalysis': '流量分析',
    'priceAnalysis': '售價分析',
    'dailySalesOrders': '每日銷售單數',
    'salesTimePeriodAnalysis': '銷售時段分析',
    'salesQuantityAnalysis': '單筆銷售數量分析',
    'differentProductsPerOrder': '單筆銷售包含不同的產品',
    
    // Store Settings
    'storeMgmt': '設定管理',
    
    // System
    'erpSystem': 'Online ERP System',
    'language': '語言',
    'chinese': '繁體中文',
    'english': 'English',
    'opening': '正在開啟',
    // Product Import page
    'productImportHeader': '滙入功能（產品資料 - HKTVmall）',
    'uploadFile': '檔案上傳',
    'downloadTemplate': '下載模板',
    'templateTip': '使用 HKTVmall 系統內的「商品管理」匯出的產品 Excel 檔',
    'chooseFile': '選擇檔案',
    'summary': '資料預覽',
    'totalRecords': '總記錄數',
    'errorCount': '錯誤數量',
    'importableRecords': '可匯入記錄',
    'confirmImport': '確認匯入',
    'cancel': '取消',
    'processing': '處理中...',
    
    // Login page
    'signInDescription': '登入以存取您的業務管理系統',
    'emailAddress': '電子郵件地址',
    'password': '密碼',
    'enterEmail': '請輸入您的電子郵件',
    'enterPassword': '請輸入您的密碼',
    'passwordRequirement': '密碼必須包含至少8個字符，包括大小寫、數字和特殊字符',
    'login': '登入',
    'signingIn': '登入中...',
    'forgotPassword': '忘記密碼？',
    'noAccount': '還沒有帳戶？',
    'registerNow': '立即註冊',
    'contactSupport': '如有任何問題，請隨時聯繫我們，我們會盡快回覆您。',
    'loginSuccess': '登入成功！',
    'loginFailed': '登入失敗',
    'invalidEmail': '請輸入有效的電子郵件地址',
    'passwordMinLength': '密碼長度至少為8個字符',
    
    // Register page
    'createAccount': '創建新帳戶',
    'joinOurERP': '加入我們的企業資源規劃系統',
    'firstName': '名字',
    'lastName': '姓氏',
    'enterFirstName': '輸入您的名字',
    'enterLastName': '輸入您的姓氏',
    'confirmPassword': '確認密碼',
    'reenterPassword': '再次輸入密碼',
    'role': '角色',
    'selectRole': '選擇您的角色',
    'generalUser': '一般用戶',
    'manager': '管理者',
    'viewer': '查看者',
    'companyAccess': '公司存取權限 (由管理員分配)',
    'adminWillAssign': '管理員將在帳戶創建後為您分配適當的公司存取權限',
    'agreeToTerms': '我同意使用條款和隱私政策',
    'termsAndPrivacy': '使用條款和隱私政策',
    'passwordRequirements': '密碼要求：',
    'atLeast8Chars': '至少8個字符',
    'upperCase': '包含大寫字母 (A-Z)',
    'lowerCase': '包含小寫字母 (a-z)',
    'numbers': '包含數字 (0-9)',
    'specialChars': '包含特殊字符 (@$!%*?&)',
    'creatingAccount': '創建帳戶中...',
    'createAccountBtn': '創建帳戶',
    'alreadyHaveAccount': '已經有帳戶了？',
    'signInNow': '立即登入',
    'backToLogin': '返回登入',
    'registrationSuccess': '註冊成功！您的帳戶正在等待管理員批准。',
    'registrationFailed': '註冊失敗，請稍後再試',
    'pleaseEnterEmail': '請輸入電子郵件地址',
    'pleaseEnterPassword': '請輸入密碼',
    'passwordTooShort': '密碼至少需要8個字符',
    'passwordComplexity': '密碼必須包含大小寫字母、數字和特殊字符',
    'pleaseConfirmPassword': '請確認密碼',
    'passwordMismatch': '密碼不一致',
    'pleaseEnterFirstName': '請輸入名字',
    'pleaseEnterLastName': '請輸入姓氏',
    'pleaseSelectRole': '請選擇角色',
    'pleaseAgreeTerms': '請同意使用條款',
    
    // Legal Documents
    'termsOfService': '使用條款',
    'privacyPolicy': '隱私政策',
    'lastUpdated': '最後更新',
    'serviceDescription': '服務說明',
    'serviceDescriptionText': '本企業資源規劃（ERP）系統為企業提供全面的業務管理解決方案，包括庫存管理、訂單處理、財務管理等功能。',
    'userAccounts': '用戶帳戶',
    'userAccountsText': '註冊使用本服務時，您同意：',
    'accountSecurity': '保護您的帳戶安全和登入憑據',
    'accurateInfo': '提供準確和最新的註冊信息',
    'unauthorizedUse': '立即通知我們任何未經授權的使用',
    'acceptableUse': '可接受使用',
    'acceptableUseText': '您同意不會：',
    'noIllegalActivity': '從事任何非法或欺詐活動',
    'noHarmfulContent': '上傳或傳輸有害內容或惡意軟件',
    'noSystemInterference': '干擾或中斷系統運作',
    'respectIntellectualProperty': '侵犯他人的知識產權',
    'dataAndPrivacy': '數據和隱私',
    'dataPrivacyText': '我們致力於保護您的個人信息和業務數據。詳細信息請參閱我們的隱私政策。',
    'paymentAndBilling': '付款和帳單',
    'paymentBillingText': '服務費用和付款條款：',
    'monthlyBilling': '按月計費，每月月初收費',
    'autoRenewal': '自動續訂，除非您取消',
    'noRefunds': '已付費用不予退還',
    'priceChanges': '價格變動將提前30天通知',
    'intellectualProperty': '知識產權',
    'intellectualPropertyText': '本服務的所有內容、軟件和知識產權均屬於我們所有。您獲得的僅為有限的使用許可。',
    'serviceAvailability': '服務可用性',
    'serviceAvailabilityText': '我們努力提供99.9%的服務正常運行時間，但不保證服務永不中斷。',
    'limitationOfLiability': '責任限制',
    'limitationLiabilityText': '在法律允許的最大範圍內，我們對任何間接、附帶或後果性損害不承擔責任。',
    'termination': '終止',
    'terminationText': '任何一方都可以通過提前30天書面通知終止本協議。',
    'governingLaw': '適用法律',
    'governingLawText': '本協議受香港特別行政區法律管轄。',
    'contactInformation': '聯繫信息',
    'contactInformationText': '如有疑問，請聯繫我們：support@erp-system.com',
    'informationCollection': '信息收集',
    'informationCollectionText': '我們收集以下類型的信息：',
    'personalInfo': '個人信息：姓名、電子郵件、電話號碼',
    'businessInfo': '業務信息：公司名稱、地址、行業類型',
    'usageData': '使用數據：功能使用情況、會話時間',
    'technicalData': '技術數據：IP地址、瀏覽器類型、設備信息',
    'howWeUseInfo': '我們如何使用信息',
    'howWeUseInfoText': '收集的信息用於：',
    'provideServices': '提供和維護我們的服務',
    'improveServices': '改進和個性化用戶體驗',
    'customerSupport': '提供客戶支持和技術協助',
    'securityMaintenance': '維護系統安全和防止欺詐',
    'legalCompliance': '遵守法律法規要求',
    'informationSharing': '信息共享',
    'informationSharingText': '我們僅在以下情況下共享您的信息：',
    'serviceProviders': '與可信的第三方服務提供商',
    'legalRequirements': '法律要求或政府要求',
    'businessTransfers': '業務轉讓或合併時',
    'consentBased': '獲得您的明確同意',
    'dataSecurity': '數據安全',
    'dataSecurityText': '我們採取以下安全措施：',
    'encryption': '數據傳輸和存儲加密',
    'accessControls': '嚴格的訪問控制和身份驗證',
    'regularAudits': '定期安全審計和漏洞評估',
    'secureDataCenters': '安全的數據中心基礎設施',
    'dataRetention': '數據保留',
    'dataRetentionText': '我們僅在必要期間保留您的數據，通常為帳戶活躍期間加上7年，除非法律要求更長的保留期。',
    'yourRights': '您的權利',
    'yourRightsText': '根據適用的數據保護法律，您擁有以下權利：',
    'accessRight': '訪問和獲取您的個人數據副本',
    'correctionRight': '更正不準確或不完整的數據',
    'deletionRight': '要求刪除您的個人數據',
    'portabilityRight': '數據可攜性和傳輸',
    'objectionRight': '反對某些數據處理活動',
    'cookiesAndTracking': 'Cookies和追蹤',
    'cookiesTrackingText': '我們使用cookies和類似技術來改善用戶體驗、分析網站使用情況和提供個性化內容。',
    'internationalTransfers': '國際數據傳輸',
    'internationalTransfersText': '您的數據可能會被傳輸到您所在國家/地區以外的地方處理。我們確保適當的保護措施到位。',
    'childrenPrivacy': '兒童隱私',
    'childrenPrivacyText': '我們的服務不向13歲以下的兒童提供。我們不會故意收集兒童的個人信息。',
    'policyChanges': '政策變更',
    'policyChangesText': '我們可能會更新本隱私政策。重大變更將通過電子郵件或服務內通知方式告知您。',
    'contactUs': '聯繫我們',
    'contactUsText': '如有隱私相關問題，請聯繫我們的數據保護官：privacy@erp-system.com',
    'and': '和',
    
    // Reset Password page
    'resetPassword': '重設密碼',
    'resetPasswordDescription': '輸入您的電子郵件地址，我們將發送重設連結給您',
    'sendResetLink': '發送重設連結',
    'sendingResetLink': '發送重設連結...',
    'setNewPassword': '設定新密碼',
    'enterNewPassword': '輸入您的新密碼',
    'newPassword': '新密碼',
    'enterNewPasswordPlaceholder': '輸入新密碼',
    'confirmNewPassword': '確認新密碼',
    'reenterNewPassword': '再次輸入新密碼',
    'updatePassword': '更新密碼',
    'updatingPassword': '更新密碼中...',
    'resetLinkSent': '重設連結已發送',
    'resetLinkSentDescription': '我們已經將密碼重設連結發送到您的電子郵件地址',
    'followSteps': '請按照以下步驟操作：',
    'checkInbox': '檢查您的電子郵件收件箱',
    'clickResetLink': '點擊重設密碼連結',
    'setNewPasswordStep': '設定您的新密碼',
    'loginWithNewPassword': '使用新密碼登入',
    'noEmailReceived': '沒有收到郵件？請檢查垃圾郵件文件夾，或',
    'resendLink': '重新發送',
    'resetLinkSuccess': '密碼重設連結已發送到您的電子郵件',
    'resetLinkFailed': '發送重設連結失敗，請稍後再試',
    'passwordUpdateSuccess': '密碼更新成功！',
    'passwordUpdateFailed': '密碼更新失敗，請稍後再試',
    'passwordComplexityError': '密碼必須至少8個字符，包含大小寫字母、數字和特殊字符',
    
    // Navigation
    'home': '首頁',
    'dashboard': '儀表板',
    'back': '返回',
    'next': '下一步',
    'previous': '上一步',
    'save': '儲存',
    'cancel': '取消',
    'delete': '刪除',
    'edit': '編輯',
    'add': '新增',
    'search': '搜尋',
    'filter': '篩選',
    'export': '匯出',
    'import': '匯入',
    'refresh': '重新整理',
    'loading': '載入中...',
    'error': '錯誤',
    'success': '成功',
    'warning': '警告',
    'info': '資訊',
    
    // Company Management
    'companies': '公司',
    'companyManagement': '公司管理',
    'createCompany': '創建公司',
    'editCompany': '編輯公司',
    'deleteCompany': '刪除公司',
    'companyDetails': '公司詳情',
    'companyName': '公司名稱',
    'companyCode': '公司代碼',
    'companyDescription': '公司描述',
    'companyAddress': '公司地址',
    'companyPhone': '聯繫電話',
    'companyEmail': '公司郵箱',
    'companyWebsite': '公司網站',
    'industry': '行業',
    'timezone': '時區',
    'currency': '貨幣',
    'active': '啟用',
    'inactive': '停用'
  },
  en: {
    // Header
    'selectCompany': 'Company List',
    'chooseCompany': 'Please select the company to access',
    'switchCompany': 'Switch Company',
    'logout': 'Logout',
    'settings': 'Settings',
    'welcomeBack': 'Welcome Back',
    'welcome': 'Hi',
    'clickToAccess': 'Click to access',
    'noAccess': 'No access',
    'adminSettings': 'Settings',
    'userManagement': 'User Management',
    'manageUserEmails': 'Manage user emails and passwords',
    'roleAssignment': 'Role Assignment',
    'assignUserRoles': 'Assign user roles and store access',
    'companyLogos': 'Company Logos',
    'uploadManageLogos': 'Manage company logos',
    'settingsFutureVersions': 'Settings functionality will be implemented in future versions',
    'accessingCompany': 'Accessing',
    'noAccessCompany': 'Do not have access to this company',
    'loggedOutSuccessfully': 'Logged out successfully',
    
    // Categories
    'orderManagement': 'Order Management',
    'productOverview': 'Product Overview',
    'purchaseSuggestions': 'Purchase Suggestions',
    'expirySummary': 'Expiry Summary',
    'promotions': 'Promotions',
    'analytics': 'Analytics',
    'storeSettings': 'Store Settings',
    'headquarters': 'Headquarters',
    'hqPurchasing': 'Purchasing',
    'hqWholesale': 'Wholesale',
    'hqAccounting': 'Accounting',

    // Order Management
    'orderOverview': 'Order Overview',
    'salesStats': 'Sales Statistics',
    'salesImport': 'Import Sales Records',
    
    // Product Overview
    'productMgmt': 'Product Management',
    'inventoryMgmt': 'Inventory Management',
    'productStats': 'Product Statistics',
    'productExpiry': 'Expiry Summary',
    'productImport': 'Import Product Data (HKTVmall)',
    'productStockImport': 'Import Stock On-hand (HKTVmall)',
    'productPendingImport': 'Import Product (Cost)',
    'product3PLDataImport': 'Import Product (3PL Data)',
    
    // Purchase Suggestions
    'suggestedOrders': 'Suggested Orders',
    'crossCompanySuggestions': 'Cross-Company Purchase Suggestions',
    'ruleSettings': 'Rule Settings',
    'purchaseAnalysis': 'Purchase Analysis',
    'purchaseImport': 'Import Purchase Data',
    
    // Expiry Summary
    'expiringSoon': 'Expiring Soon',
    'expiryHandling': 'Expiry Verification',
    'bestExpiryImport': 'Import (Best Expiry Date)',
    'bestExpiryImportDesc': 'Auto-analyze and compare expiry dates after Excel import',
    'columnIdentification': 'Column Identification',
    'skuColumn': 'SKU Column',
    'dateColumn': 'Date Column',
    'processingStatus': 'Processing Status',
    'resultsSummary': 'Results Summary',
    'totalRecords': 'Total Records',
    'validRecords': 'Valid Records',
    'skippedRecords': 'Skipped Records',
    'matchedRecords': 'Matched Records',
    'itemsToReview': 'Items to Review',
    'remindersList': 'Reminders List',
    'expiryMismatch': 'Expiry Date Mismatch',
    'excelExpiryDate': 'Excel Expiry Date',
    'systemExpiryDate': 'System Expiry Date',
    'columnNotFound': 'Required columns not found',
    'supportedColumns': 'Supported Column Names',
    'skuColumnNames': 'SKU Columns: 条码, Barcode',
    'dateColumnNames': 'Date Columns: 批次号, 有效日期, Expiry Date',
    'invalidDataSkipped': 'Invalid data has been skipped',
    'processingComplete': 'Processing Complete',
    
    // Promotions
    'campaignOverview': 'Campaign Overview',
    'searchCampaigns': 'Search Campaigns',
    'promotionImport': 'Import Promotional Data',
    
    // Analytics
    'salesAnalysis': 'Sales Analysis',
    'stockAnalysis': 'Stock Analysis',
    'slowMoving': 'Slow-moving Products',
    'trafficAnalysis': 'Traffic Analysis',
    'priceAnalysis': 'Price Analysis',
    'dailySalesOrders': 'Daily Sales Orders',
    'salesTimePeriodAnalysis': 'Sales Time Period Analysis',
    'salesQuantityAnalysis': 'Sales Quantity Analysis',
    'differentProductsPerOrder': 'Different Products Per Order',
    
    // Store Settings
    'storeMgmt': 'Access, Permissions, Appearance',
    
    // System
    'erpSystem': 'Online ERP System',
    'language': 'Language',
    'chinese': '繁體中文',
    'english': 'English',
    'opening': 'Opening',
    // Product Import page
    'productImportHeader': 'Import Product Data (HKTVmall)',
    'uploadFile': 'File Upload',
    'downloadTemplate': 'Download Template',
    'templateTip': 'Use product Excel file exported from HKTVmall system\'s "Product Management"',
    'chooseFile': 'Choose File',
    'summary': 'Summary',
    'totalRecords': 'Total Records',
    'errorCount': 'Errors',
    'importableRecords': 'Importable',
    'confirmImport': 'Confirm Import',
    'cancel': 'Cancel',
    'processing': 'Processing...',
    
    // Login page
    'signInDescription': 'Sign in to access your business management system',
    'emailAddress': 'Email Address',
    'password': 'Password',
    'enterEmail': 'Enter your email',
    'enterPassword': 'Enter your password',
    'passwordRequirement': 'Password must contain at least 8 characters, including uppercase, lowercase, number, and special character',
    'login': 'Login',
    'signingIn': 'Signing In...',
    'forgotPassword': 'Forgot Password?',
    'noAccount': 'Don\'t have an account?',
    'registerNow': 'Register Now',
    'contactSupport': 'If you have any questions, please feel free to contact us and we will get back to you as soon as possible.',
    'loginSuccess': 'Login successful!',
    'loginFailed': 'Login failed',
    'invalidEmail': 'Please enter a valid email address',
    'passwordMinLength': 'Password must be at least 8 characters long',
    
    // Register page
    'createAccount': 'Create New Account',
    'joinOurERP': 'Join our Enterprise Resource Planning system',
    'firstName': 'First Name',
    'lastName': 'Last Name',
    'enterFirstName': 'Enter your first name',
    'enterLastName': 'Enter your last name',
    'confirmPassword': 'Confirm Password',
    'reenterPassword': 'Re-enter your password',
    'role': 'Role',
    'selectRole': 'Select your role',
    'generalUser': 'General User',
    'manager': 'Manager',
    'viewer': 'Viewer',
    'companyAccess': 'Company Access (Assigned by Admin)',
    'adminWillAssign': 'Administrator will assign appropriate company access after account creation',
    'agreeToTerms': 'I agree to the Terms of Service and Privacy Policy',
    'termsAndPrivacy': 'Terms of Service and Privacy Policy',
    'passwordRequirements': 'Password Requirements:',
    'atLeast8Chars': 'At least 8 characters',
    'upperCase': 'Contains uppercase letters (A-Z)',
    'lowerCase': 'Contains lowercase letters (a-z)',
    'numbers': 'Contains numbers (0-9)',
    'specialChars': 'Contains special characters (@$!%*?&)',
    'creatingAccount': 'Creating Account...',
    'createAccountBtn': 'Create Account',
    'alreadyHaveAccount': 'Already have an account?',
    'signInNow': 'Sign In Now',
    'backToLogin': 'Back to Login',
    'registrationSuccess': 'Registration successful! Your account is pending admin approval.',
    'registrationFailed': 'Registration failed, please try again later',
    'pleaseEnterEmail': 'Please enter your email address',
    'pleaseEnterPassword': 'Please enter a password',
    'passwordTooShort': 'Password must be at least 8 characters',
    'passwordComplexity': 'Password must contain uppercase, lowercase, numbers and special characters',
    'pleaseConfirmPassword': 'Please confirm your password',
    'passwordMismatch': 'Passwords do not match',
    'pleaseEnterFirstName': 'Please enter your first name',
    'pleaseEnterLastName': 'Please enter your last name',
    'pleaseSelectRole': 'Please select a role',
    'pleaseAgreeTerms': 'Please agree to the terms of service',
    
    // Legal Documents
    'termsOfService': 'Terms of Service',
    'privacyPolicy': 'Privacy Policy',
    'lastUpdated': 'Last Updated',
    'serviceDescription': 'Service Description',
    'serviceDescriptionText': 'This Enterprise Resource Planning (ERP) system provides comprehensive business management solutions including inventory management, order processing, financial management, and more.',
    'userAccounts': 'User Accounts',
    'userAccountsText': 'By registering for our service, you agree to:',
    'accountSecurity': 'Protect your account security and login credentials',
    'accurateInfo': 'Provide accurate and up-to-date registration information',
    'unauthorizedUse': 'Immediately notify us of any unauthorized use',
    'acceptableUse': 'Acceptable Use',
    'acceptableUseText': 'You agree not to:',
    'noIllegalActivity': 'Engage in any illegal or fraudulent activities',
    'noHarmfulContent': 'Upload or transmit harmful content or malware',
    'noSystemInterference': 'Interfere with or disrupt system operations',
    'respectIntellectualProperty': 'Infringe on others\' intellectual property rights',
    'dataAndPrivacy': 'Data and Privacy',
    'dataPrivacyText': 'We are committed to protecting your personal information and business data. For details, please refer to our Privacy Policy.',
    'paymentAndBilling': 'Payment and Billing',
    'paymentBillingText': 'Service fees and payment terms:',
    'monthlyBilling': 'Monthly billing, charged at the beginning of each month',
    'autoRenewal': 'Automatic renewal unless you cancel',
    'noRefunds': 'Paid fees are non-refundable',
    'priceChanges': 'Price changes will be notified 30 days in advance',
    'intellectualProperty': 'Intellectual Property',
    'intellectualPropertyText': 'All content, software, and intellectual property of this service belong to us. You receive only a limited license to use.',
    'serviceAvailability': 'Service Availability',
    'serviceAvailabilityText': 'We strive to provide 99.9% service uptime, but do not guarantee uninterrupted service.',
    'limitationOfLiability': 'Limitation of Liability',
    'limitationLiabilityText': 'To the maximum extent permitted by law, we are not liable for any indirect, incidental, or consequential damages.',
    'termination': 'Termination',
    'terminationText': 'Either party may terminate this agreement with 30 days\' written notice.',
    'governingLaw': 'Governing Law',
    'governingLawText': 'This agreement is governed by the laws of Hong Kong Special Administrative Region.',
    'contactInformation': 'Contact Information',
    'contactInformationText': 'For questions, please contact us: support@erp-system.com',
    'informationCollection': 'Information Collection',
    'informationCollectionText': 'We collect the following types of information:',
    'personalInfo': 'Personal Information: Name, email, phone number',
    'businessInfo': 'Business Information: Company name, address, industry type',
    'usageData': 'Usage Data: Feature usage, session duration',
    'technicalData': 'Technical Data: IP address, browser type, device information',
    'howWeUseInfo': 'How We Use Information',
    'howWeUseInfoText': 'Collected information is used for:',
    'provideServices': 'Providing and maintaining our services',
    'improveServices': 'Improving and personalizing user experience',
    'customerSupport': 'Providing customer support and technical assistance',
    'securityMaintenance': 'Maintaining system security and preventing fraud',
    'legalCompliance': 'Complying with legal and regulatory requirements',
    'informationSharing': 'Information Sharing',
    'informationSharingText': 'We only share your information in the following circumstances:',
    'serviceProviders': 'With trusted third-party service providers',
    'legalRequirements': 'Legal requirements or government requests',
    'businessTransfers': 'During business transfers or mergers',
    'consentBased': 'With your explicit consent',
    'dataSecurity': 'Data Security',
    'dataSecurityText': 'We implement the following security measures:',
    'encryption': 'Data transmission and storage encryption',
    'accessControls': 'Strict access controls and authentication',
    'regularAudits': 'Regular security audits and vulnerability assessments',
    'secureDataCenters': 'Secure data center infrastructure',
    'dataRetention': 'Data Retention',
    'dataRetentionText': 'We retain your data only for as long as necessary, typically during account activity plus 7 years, unless longer retention is required by law.',
    'yourRights': 'Your Rights',
    'yourRightsText': 'Under applicable data protection laws, you have the following rights:',
    'accessRight': 'Access and obtain copies of your personal data',
    'correctionRight': 'Correct inaccurate or incomplete data',
    'deletionRight': 'Request deletion of your personal data',
    'portabilityRight': 'Data portability and transfer',
    'objectionRight': 'Object to certain data processing activities',
    'cookiesAndTracking': 'Cookies and Tracking',
    'cookiesTrackingText': 'We use cookies and similar technologies to improve user experience, analyze website usage, and provide personalized content.',
    'internationalTransfers': 'International Data Transfers',
    'internationalTransfersText': 'Your data may be transferred to and processed in locations outside your country/region. We ensure appropriate safeguards are in place.',
    'childrenPrivacy': 'Children\'s Privacy',
    'childrenPrivacyText': 'Our services are not intended for children under 13. We do not knowingly collect personal information from children.',
    'policyChanges': 'Policy Changes',
    'policyChangesText': 'We may update this privacy policy. Significant changes will be communicated via email or in-service notifications.',
    'contactUs': 'Contact Us',
    'contactUsText': 'For privacy-related questions, please contact our Data Protection Officer: privacy@erp-system.com',
    'and': 'and',
    
    // Reset Password page
    'resetPassword': 'Reset Password',
    'resetPasswordDescription': 'Enter your email address and we will send you a reset link',
    'sendResetLink': 'Send Reset Link',
    'sendingResetLink': 'Sending Reset Link...',
    'setNewPassword': 'Set New Password',
    'enterNewPassword': 'Enter your new password',
    'newPassword': 'New Password',
    'enterNewPasswordPlaceholder': 'Enter new password',
    'confirmNewPassword': 'Confirm New Password',
    'reenterNewPassword': 'Re-enter new password',
    'updatePassword': 'Update Password',
    'updatingPassword': 'Updating Password...',
    'resetLinkSent': 'Reset Link Sent',
    'resetLinkSentDescription': 'We have sent a password reset link to your email address',
    'followSteps': 'Please follow these steps:',
    'checkInbox': 'Check your email inbox',
    'clickResetLink': 'Click the reset password link',
    'setNewPasswordStep': 'Set your new password',
    'loginWithNewPassword': 'Login with your new password',
    'noEmailReceived': 'Did not receive email? Please check your spam folder, or',
    'resendLink': 'resend',
    'resetLinkSuccess': 'Password reset link has been sent to your email',
    'resetLinkFailed': 'Failed to send reset link, please try again later',
    'passwordUpdateSuccess': 'Password updated successfully!',
    'passwordUpdateFailed': 'Password update failed, please try again later',
    'passwordComplexityError': 'Password must be at least 8 characters with uppercase, lowercase, numbers and special characters',
    
    // Navigation
    'home': 'Home',
    'dashboard': 'Dashboard',
    'back': 'Back',
    'next': 'Next',
    'previous': 'Previous',
    'save': 'Save',
    'cancel': 'Cancel',
    'delete': 'Delete',
    'edit': 'Edit',
    'add': 'Add',
    'search': 'Search',
    'filter': 'Filter',
    'export': 'Export',
    'import': 'Import',
    'refresh': 'Refresh',
    'loading': 'Loading...',
    'error': 'Error',
    'success': 'Success',
    'warning': 'Warning',
    'info': 'Information',
    
    // Company Management
    'companies': 'Companies',
    'companyManagement': 'Company Management',
    'createCompany': 'Create Company',
    'editCompany': 'Edit Company',
    'deleteCompany': 'Delete Company',
    'companyDetails': 'Company Details',
    'companyName': 'Company Name',
    'companyCode': 'Company Code',
    'companyDescription': 'Company Description',
    'companyAddress': 'Company Address',
    'companyPhone': 'Phone Number',
    'companyEmail': 'Company Email',
    'companyWebsite': 'Company Website',
    'industry': 'Industry',
    'timezone': 'Timezone',
    'currency': 'Currency',
    'active': 'Active',
    'inactive': 'Inactive'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Step 2: Initial state from localStorage first to avoid flicker (conservative: same "global" state, sourced from localStorage)
  const [language, setLanguageState] = useState<Language>(getStoredLanguage);

  // Step 2: Sync language from Supabase profiles.language (if has value, overwrite state + localStorage)
  const fetchSupabaseProfile = useCallback(
    async (userId: string, isCancelled: { current: boolean }) => {
      const { data } = await supabase
        .from('profiles')
        .select('language')
        .eq('id', userId)
        .single();
      if (isCancelled.current) return;
      const serverLang = data?.language;
      if (serverLang === 'zh' || serverLang === 'en') {
        setLanguageState(serverLang);
        try {
          localStorage.setItem(LANGUAGE_STORAGE_KEY, serverLang);
        } catch {
          // ignore
        }
      }
      // If NULL: do nothing (no auto write-back per TASK-030)
    },
    []
  );

  useEffect(() => {
    const cancelledRef = { current: false };

    const runSync = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id || cancelledRef.current) return;
      await fetchSupabaseProfile(session.user.id, cancelledRef);
    };

    runSync();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelledRef.current) return;
      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session?.user?.id) {
        fetchSupabaseProfile(session.user.id, cancelledRef);
      }
    });

    return () => {
      cancelledRef.current = true;
      subscription?.unsubscribe();
    };
  }, [fetchSupabaseProfile]);

  // Step 3: On user click switch -> update UI + localStorage + Supabase (if logged in)
  const handleSetLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    } catch {
      // ignore
    }
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      await supabase.from('profiles').update({ language: lang }).eq('id', session.user.id);
      // On error: do not rollback UI or localStorage (per TASK-030)
    })();
  }, []);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
